import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getPublicEvents, PublicEvent } from "../../api/publicEventsAPI";
import { Calendar, MapPin, Clock } from "lucide-react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";

interface PublicEventsListPageProps {
  embed?: boolean;
}

const PublicEventsListPage: React.FC<PublicEventsListPageProps> = ({
  embed,
}) => {
  const { clubid } = useParams<{ clubid: string }>();
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (clubid) {
      fetchEvents();
    }
  }, [clubid]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPublicEvents(clubid!);
      if (response?.success) {
        setEvents(response.data || []);
      } else {
        setError("Failed to load events");
      }
    } catch {
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const [year, month, day] = dateString.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getDateRange = (event: PublicEvent) => {
    if (!event.dates || event.dates.length === 0) return "Dates TBD";
    const firstDate = event.dates[0].date;
    const lastDate = event.dates[event.dates.length - 1].date;
    if (firstDate === lastDate) return formatDate(firstDate);
    return `${formatDate(firstDate)} - ${formatDate(lastDate)}`;
  };

  const getLowestPrice = (event: PublicEvent) => {
    if (!event.pricing_options || event.pricing_options.length === 0) return "Free";
    const prices = event.pricing_options.map((p) => p.amount).filter((a) => a > 0);
    if (prices.length === 0) return "Free";
    const lowest = Math.min(...prices);
    return `$${lowest.toFixed(2)}`;
  };

  const getSpotsText = (event: PublicEvent) => {
    if (!event.max_registrations) return null;
    const remaining = event.max_registrations - event.current_registrations;
    if (remaining <= 0) return event.waitlist_enabled ? "Waitlist available" : "Full";
    if (remaining <= 5) return `${remaining} spots left`;
    return `${remaining} spots available`;
  };

  const getTimeRange = (event: PublicEvent) => {
    if (!event.dates || event.dates.length === 0) return null;
    const first = event.dates[0];
    if (!first.start_time) return null;
    const formatTime = (t: string) => {
      const [hours, minutes] = t.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };
    if (first.end_time) {
      return `${formatTime(first.start_time)} - ${formatTime(first.end_time)}`;
    }
    return formatTime(first.start_time);
  };

  if (loading) {
    return (
      <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
        {!embed && <Navbar />}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 dark:border-gray-500" role="status" aria-label="Loading"></div>
        </div>
        {!embed && <Footer />}
      </div>
    );
  }

  return (
    <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
      {!embed && <Navbar />}
      <div className="flex-1 max-w-6xl mx-auto px-4 py-8 w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Upcoming Events
          </h1>
          <p className="text-gray-600">
            Browse our upcoming events and activities.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {events.length === 0 && !error ? (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-500 mb-2">
              No events available
            </h2>
            <p className="text-gray-400">
              Check back later for upcoming events.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const timeRange = getTimeRange(event);
              const spotsText = getSpotsText(event);
              return (
                <Link
                  key={event.id}
                  to={
                    embed
                      ? `/${clubid}/embed/events/${event.id}`
                      : `/${clubid}/events/${event.id}`
                  }
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {event.cover_image_url && (
                    <img
                      src={event.cover_image_url}
                      alt={event.name}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-5">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {event.category && (
                        <span
                          className="inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full"
                          style={{
                            backgroundColor: `${event.category.color}20`,
                            color: event.category.color,
                          }}
                        >
                          {event.category.name}
                        </span>
                      )}
                      {event.members_only && (
                        <span className="inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                          Members Only
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {event.name}
                    </h3>

                    {event.description && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {event.description}
                      </p>
                    )}

                    <div className="space-y-2 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>{getDateRange(event)}</span>
                      </div>

                      {timeRange && (
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{timeRange}</span>
                        </div>
                      )}

                      {event.dates?.[0]?.location && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="truncate">
                            {event.dates[0].location}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-lg font-bold text-blue-600">
                        {getLowestPrice(event)}
                      </span>
                      {spotsText && (
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${
                            spotsText === "Full"
                              ? "bg-red-100 text-red-700"
                              : spotsText.includes("left")
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700"
                          }`}
                        >
                          {spotsText}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      {!embed && <Footer />}
    </div>
  );
};

export default PublicEventsListPage;
